import { RotateCcw, Scale } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import type { ComparedJourney } from '@ederest/contracts';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../../shared/ui/Button.js';
import { Card } from '../../shared/ui/Card.js';
import { ExportMenu } from '../../shared/ui/ExportMenu.js';
import { PageHeader } from '../../shared/layout/PageHeader.js';
import { useComparison, useComparisons } from '../../shared/api/queries.js';
import { useActiveProject } from '../../shared/api/useActiveProject.js';
import { ComparisonReading } from './ComparisonReading.js';
import { ComparisonSummary } from './ComparisonSummary.js';
import { ComparisonTable } from './ComparisonTable.js';
import { DirectionChip } from './DirectionChip.js';
import '../../shared/ui/filters.css';
import './comparison.css';
import './dashboard.css';
import { LowSampleBadge } from '../../shared/ui/LowSampleBadge.js';

const VIEWPORT_LABELS: Record<string, string> = {
  mobile: 'Téléphone',
  tablet: 'Tablette',
  desktop: 'Ordinateur',
};
const LANGUAGE_LABELS: Record<string, string> = { fr: 'Français', ar: 'Arabe', zgh: 'Amazighe' };
const CRITERIA = ['journeyId', 'language', 'viewport'] as const;

/**
 * "Avant / après" view of the dashboard zone (functional §5.1, §5.4): the proper
 * effect of a published version, same journey, equivalent duration. The version
 * and the criteria live in the URL so a comparison can be shared.
 */
export function ComparisonPage() {
  const { project } = useActiveProject();
  const [params, setParams] = useSearchParams();
  const { data: list } = useComparisons(project?.id);

  const requested = params.get('version');
  const version = requested ? Number(requested) : list?.items[0]?.releaseVersion;
  const criteria = useMemo(() => {
    const query = new URLSearchParams();
    for (const key of CRITERIA) {
      const value = params.get(key);
      if (value) query.set(key, value);
    }
    return query.toString();
  }, [params]);
  const { data, isLoading, error } = useComparison(project?.id, version, criteria);

  const set = useCallback(
    (patch: Record<string, string | undefined>) => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          for (const [key, value] of Object.entries(patch)) {
            if (value === undefined || value === '') next.delete(key);
            else next.set(key, value);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );
  const activeCount = CRITERIA.filter((key) => params.has(key)).length;
  const exportQuery = new URLSearchParams(criteria);
  if (version) exportQuery.set('releaseVersion', String(version));

  const journeys = data?.journeys ?? [];
  const journeyOptions = useMemo(
    () =>
      journeys
        .map((journey) => [journey.journeyId, journey.journeyName] as const)
        .sort(([, a], [, b]) => a.localeCompare(b)),
    [journeys],
  );
  const selectedJourney = params.get('journeyId') ?? '';
  // The journeys a published guidance sits on carry the effect; the others are context, folded.
  const guided = journeys.filter((journey) => journey.steps.some((step) => step.hasGuidance));
  const others = journeys.filter((journey) => !journey.steps.some((step) => step.hasGuidance));
  const [showOthers, setShowOthers] = useState(false);
  // A journey picked in the filter is shown whatever its group.
  const othersOpen = showOthers || selectedJourney !== '';

  return (
    <>
      <PageHeader
        title="Avant / après"
        description={
          project
            ? `Projet ${project.name} · ce qui a changé pour les usagers depuis la publication d'une aide`
            : undefined
        }
        actions={
          <ExportMenu
            projectId={project?.id}
            queryString={exportQuery.toString()}
            datasets={['comparison']}
          />
        }
      />

      <div className="filters" role="group" aria-label="Critères de la comparaison">
        <label className="comparison__version">
          <span>Version comparée</span>
          <select
            aria-label="Choisir la version comparée"
            value={version ? String(version) : ''}
            onChange={(e) => set({ version: e.target.value })}
            disabled={!list || list.items.length === 0}
          >
            {(list?.items ?? []).map((item) => (
              <option key={item.releaseVersion} value={item.releaseVersion}>
                Version {item.releaseVersion} · publiée le{' '}
                {new Date(item.publishedAt).toLocaleDateString('fr-FR')}
              </option>
            ))}
            {list && list.items.length === 0 && (
              <option value="">Aucune version mesurée avant et après</option>
            )}
          </select>
        </label>

        <label className="filters__field">
          <span>Parcours</span>
          <select
            aria-label="Filtrer par parcours"
            value={selectedJourney}
            onChange={(e) => set({ journeyId: e.target.value })}
          >
            <option value="">Tous les parcours</option>
            {journeyOptions.map(([id, name]) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
            {selectedJourney && !journeyOptions.some(([id]) => id === selectedJourney) && (
              <option value={selectedJourney}>Parcours sélectionné</option>
            )}
          </select>
        </label>

        <label className="filters__field">
          <span>Langue</span>
          <select
            aria-label="Filtrer par langue"
            value={params.get('language') ?? ''}
            onChange={(e) => set({ language: e.target.value })}
          >
            <option value="">Toutes les langues</option>
            {(data?.dimensions.languages ?? []).map((language) => (
              <option key={language} value={language}>
                {LANGUAGE_LABELS[language] ?? language}
              </option>
            ))}
          </select>
        </label>

        <label className="filters__field">
          <span>Appareil</span>
          <select
            aria-label="Filtrer par appareil"
            value={params.get('viewport') ?? ''}
            onChange={(e) => set({ viewport: e.target.value })}
          >
            <option value="">Tous les appareils</option>
            {(data?.dimensions.viewports ?? []).map((viewport) => (
              <option key={viewport} value={viewport}>
                {VIEWPORT_LABELS[viewport] ?? viewport}
              </option>
            ))}
          </select>
        </label>

        {activeCount > 0 && (
          <Button
            variant="ghost"
            icon={<RotateCcw />}
            onClick={() => set({ journeyId: undefined, language: undefined, viewport: undefined })}
            aria-label="Réinitialiser les critères"
          >
            Réinitialiser
          </Button>
        )}
      </div>

      {list && list.items.length === 0 && (
        <Card title="Aucune comparaison disponible" icon={<Scale size={15} />} className="dash__section">
          <p className="comparison__empty" data-testid="comparison-empty">
            {list.activeReleaseVersion === null
              ? "Aucune guidance n'a encore été publiée sur ce projet. La comparaison s'ouvre après la première publication, dès que des sessions sont mesurées sous la nouvelle version."
              : `La version ${list.activeReleaseVersion} est en ligne, mais aucun usager n'a encore été mesuré sous cette version. La comparaison apparaîtra après la prochaine analyse.`}
          </p>
        </Card>
      )}

      {error && !data && (
        <Card className="dash__section">
          <p className="comparison__empty" role="alert">
            Cette version n'a pas de mesure avant / après.
          </p>
        </Card>
      )}

      {isLoading && !data && <p className="dash__loading">Chargement de la comparaison…</p>}

      {data && (
        <>
          <ComparisonReading comparison={data} guided={guided} othersCount={others.length} />
          <ComparisonSummary summary={data} />
          {journeys.length === 0 && (
            <Card className="dash__section">
              <p className="comparison__empty">Aucune session mesurée des deux côtés pour ces critères.</p>
            </Card>
          )}
          {guided.map((journey) => (
            <JourneyCard key={journey.journeyId} journey={journey} />
          ))}
          {others.length > 0 && (
            <section className="comparison__others" aria-label="Parcours sans aide publiée">
              <button
                type="button"
                className="comparison__others-toggle"
                aria-expanded={othersOpen}
                onClick={() => setShowOthers((value) => !value)}
                data-testid="comparison-others-toggle"
              >
                {othersOpen ? 'Masquer' : 'Afficher'} {others.length} parcours sans aide publiée
              </button>
              <p className="comparison__others-note">
                Aucune aide de cette version ne s'y affiche : leurs écarts ne viennent pas de la publication
                (variations naturelles de la fréquentation).
              </p>
              {othersOpen &&
                others.map((journey) => <JourneyCard key={journey.journeyId} journey={journey} />)}
            </section>
          )}
          <p className="dash__note">
            Un écart de moins d'un point (taux, scores) ou de moins de 5 % (durées) est lu comme stable. « Peu
            d'usagers » : moins de {data.minimumSessionCount} usagers mesurés d'un côté ou de l'autre, chiffre
            à lire avec prudence. Survolez un en-tête de colonne pour sa définition.
          </p>
        </>
      )}
    </>
  );
}

/** One journey: its users and headline movements in the header, its steps in the table. */
function JourneyCard({ journey }: { journey: ComparedJourney }) {
  const completion = journey.indicators.find((delta) => delta.key === 'completion_rate');
  const friction = journey.indicators.find((delta) => delta.key === 'friction_score');
  return (
    <Card
      title={journey.journeyName}
      icon={<Scale size={15} />}
      padded={false}
      className="dash__section"
      actions={
        <div className="comparison__journey-head">
          <span>
            {journey.beforeSessions.toLocaleString('fr-FR')} → {journey.afterSessions.toLocaleString('fr-FR')}{' '}
            usagers
          </span>
          {completion && (
            <span title="Part des usagers qui vont au bout du parcours.">
              Vont au bout <DirectionChip delta={completion} />
            </span>
          )}
          {friction && (
            <span title="Difficulté mesurée sur le parcours, de 0 à 100. Elle doit baisser.">
              Friction <DirectionChip delta={friction} />
            </span>
          )}
          {journey.lowConfidence && <LowSampleBadge />}
        </div>
      }
    >
      <ComparisonTable journey={journey} />
    </Card>
  );
}
